// src/support/salesforceTeamActivity.ts
//
// Activity counts for the Salesforce tab's team dashboard and "By Team Member" table, all read from the SAME
// source: the `sf/team/se-activity-summary/` tree. Every node there carries `rollup` (that person plus
// everyone under them), so a manager's figures are always their team's rollup — the dashboard cards, the
// by-report charts and the table can never disagree.

import type { SeActivityNode } from '../hooks/useSfTeamActivitySummary';
import type { ChartPoint } from './campaignLeaderCharts';
import { shortName } from './campaignLeaderCharts';

/** The server's activity categories, in display order (`activity_type_counts` keys). */
export const ACTIVITY_CATEGORIES = [
  'POC/POV',
  'Demo',
  'Workshop',
  'Design/Whiteboard',
  'Briefing/EBC',
  'Trial',
  'Other',
] as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];
export type ActivityCounts = Record<ActivityCategory, number>;

const norm = (v: string) => String(v ?? '').trim().toLowerCase();

function countsFrom(record: Record<string, number>): ActivityCounts {
  const byKey = new Map<string, number>();
  for (const [k, n] of Object.entries(record)) byKey.set(norm(k), (byKey.get(norm(k)) ?? 0) + (Number(n) || 0));
  const out = {} as ActivityCounts;
  for (const c of ACTIVITY_CATEGORIES) out[c] = byKey.get(norm(c)) ?? 0;
  return out;
}

/** The selected leader's own team total per category (their `rollup`: themselves plus every descendant). */
export function teamActivityTotals(tree: SeActivityNode | null | undefined): ActivityCounts | null {
  const counts = tree?.rollup?.activity_type_counts;
  return counts ? countsFrom(counts) : null;
}

/**
 * Per-category counts for each DIRECT report of the tree's root, keyed by lower-cased email. A sub-manager's
 * figures are their whole team's (`rollup`), an IC's are their own (their rollup is their own). Null when the
 * tree is missing or the server sent no activity counts, so callers can show "—" rather than a false 0.
 */
export function activityCountsByReport(
  tree: SeActivityNode | null | undefined,
): Map<string, ActivityCounts> | null {
  if (!tree?.rollup?.activity_type_counts) return null;
  const reports = Object.values(tree.reports ?? {});
  if (reports.some((r) => !r.rollup?.activity_type_counts)) return null;
  const out = new Map<string, ActivityCounts>();
  for (const r of reports) out.set(norm(r.email), countsFrom(r.rollup.activity_type_counts ?? {}));
  return out;
}

/** One bar/slice per direct report for a category (their rollup); reports with none are left out. */
export function activityByReportRows(
  tree: SeActivityNode | null | undefined,
  category: ActivityCategory,
): ChartPoint[] {
  const byReport = activityCountsByReport(tree);
  if (!byReport) return [];
  const rows: ChartPoint[] = [];
  for (const [email, counts] of Array.from(byReport.entries())) {
    if (counts[category] > 0) rows.push({ label: shortName(email), value: counts[category] });
  }
  return rows;
}
